import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';

const FOUNDER_IMAGE = "https://customer-assets.emergentagent.com/job_4e491698-f834-49a8-a1e5-8fd3efa88feb/artifacts/35vjb36h_FB_IMG_1772976275108.jpg";

export default function FounderSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="py-20 md:py-32 bg-white"
      data-testid="founder-section"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-6"
        >
          <span className="inline-block px-4 py-2 bg-ba-accent/30 text-ba-primary text-sm font-medium rounded-full">
            L'histoire du fondateur
          </span>
        </motion.div>

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-3xl md:text-5xl font-bold text-rm-text text-center mb-12"
        >
          Créé par un entrepreneur,{' '}
          <span className="text-gradient">pour les entrepreneurs</span>
        </motion.h2>

        {/* Photo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative mb-12 max-w-2xl mx-auto"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <img 
              src={FOUNDER_IMAGE}
              alt="Romain - Fondateur de BatiAssist"
              className="w-full h-auto object-cover"
            />
          </div>
          {/* Experience badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.5 }}
            className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 bg-ba-primary text-white rounded-2xl p-4 md:p-5 shadow-xl"
          >
            <div className="text-2xl md:text-3xl font-bold">27+</div>
            <div className="text-xs md:text-sm opacity-80">ans d'expérience</div>
          </motion.div>
        </motion.div>

        {/* Text content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="space-y-6 text-rm-text-secondary leading-relaxed text-center md:text-left"
        >
          <p className="text-lg">
            Je suis entrepreneur depuis l'âge de 20 ans. Depuis plus de 27 ans, 
            j'ai créé et développé plusieurs entreprises dans différents secteurs.
          </p>
          
          <p>
            J'ai notamment créé et géré trois clubs de remise en forme, dirigé le plus grand 
            complexe de remise en forme d'Afrique, créé une agence marketing, et bâti une 
            entreprise dans l'univers de la piscine avec plus de 1500 piscines construites dans 7 pays.
          </p>
          
          <p>
            Mais comme beaucoup d'entrepreneurs, j'ai aussi connu la réalité du terrain : 
            trop d'opérationnel, trop de responsabilités, et une entreprise qui finit par 
            dépendre entièrement de toi. En 2023, j'ai tout perdu.
          </p>
          
          <p>
            J'ai pris une décision radicale : partir à Madagascar pendant 10 mois pour 
            reconstruire un nouveau système. Un système basé sur des assistants humains, 
            des outils d'automatisation et l'intelligence artificielle.
          </p>
          
          <p className="font-medium text-rm-text text-lg">
            C'est de cette experience qu'est ne BatiAssist. Aujourd'hui, je partage 
            ce systeme avec d'autres entrepreneurs pour les aider a ne pas vivre ce que j'ai vecu.
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="flex justify-center mt-10"
        >
          <Link to="/a-propos">
            <Button 
              className="bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full px-8 py-6 shadow-lg hover:shadow-xl transition-all hover:scale-105 group"
              data-testid="founder-cta"
            >
              Lire l'histoire complète
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
